import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  MessageSquare,
  Plus,
  Trash2,
  Loader2,
  Pencil,
  Check,
  X,
  Search,
  MoreVertical,
  History,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDistanceToNow, isToday, isYesterday, subDays, isAfter } from 'date-fns'
import type { Conversation } from '@/lib/api/chat'
import { useState, useMemo } from 'react'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface ConversationListProps {
  conversations: Conversation[]
  currentConversationId: string | null
  onSelectConversation: (id: string) => void
  onNewConversation: () => void
  onDeleteConversation: (id: string) => void
  onUpdateConversation?: (id: string, title: string) => Promise<void>
  isLoading: boolean
}

type DateGroup = 'Today' | 'Yesterday' | 'Previous 7 Days' | 'Older'

export function ConversationList({
  conversations,
  currentConversationId,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
  onUpdateConversation,
  isLoading,
}: ConversationListProps) {
  const [searchTerm, setSearchTerm] = useState('')

  const filteredConversations = useMemo(() => {
    if (!searchTerm.trim()) return conversations
    return conversations.filter((c) => c.title.toLowerCase().includes(searchTerm.toLowerCase()))
  }, [conversations, searchTerm])

  const groupedConversations = useMemo(() => {
    const groups: Record<DateGroup, Conversation[]> = {
      Today: [],
      Yesterday: [],
      'Previous 7 Days': [],
      Older: [],
    }

    const sevenDaysAgo = subDays(new Date(), 7)

    filteredConversations.forEach((conversation) => {
      const date = new Date(conversation.updated_at)
      if (isToday(date)) {
        groups['Today'].push(conversation)
      } else if (isYesterday(date)) {
        groups['Yesterday'].push(conversation)
      } else if (isAfter(date, sevenDaysAgo)) {
        groups['Previous 7 Days'].push(conversation)
      } else {
        groups['Older'].push(conversation)
      }
    })

    return groups
  }, [filteredConversations])

  const hasResults = filteredConversations.length > 0

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border flex-shrink-0 space-y-3 bg-transparent">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={onNewConversation}
              className="w-full shadow-sm hover:shadow-md transition-all active:scale-[0.98] bg-muted/20 hover:bg-muted/40 text-foreground border border-border/50"
              size="lg"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Chat
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Start a new conversation to test MCP tools</p>
          </TooltipContent>
        </Tooltip>

        {/* Search Input */}
        <div className="relative group">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
          <Input
            placeholder="Search conversations..."
            className="pl-9 pr-8 h-9 text-xs bg-muted/20 focus:bg-muted/30 border-border/50 transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-2.5 top-2.5 p-0.5 hover:bg-muted rounded-full transition-colors"
            >
              <X className="h-3 w-3 text-muted-foreground" />
            </button>
          )}
        </div>
      </div>

      {/* Conversation List */}
      <ScrollArea className="flex-1">
        <div className="p-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : !hasResults ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center mb-3">
                {searchTerm ? (
                  <Search className="w-6 h-6 text-muted-foreground" />
                ) : (
                  <MessageSquare className="w-6 h-6 text-muted-foreground" />
                )}
              </div>
              <p className="text-sm text-muted-foreground font-medium">
                {searchTerm ? 'No results found' : 'No conversations yet'}
              </p>
              <p className="text-xs text-muted-foreground mt-1 mb-4">
                {searchTerm ? `No matches for "${searchTerm}"` : 'Start a new chat to begin'}
              </p>
              {searchTerm && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSearchTerm('')}
                  className="h-8"
                >
                  Clear search
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {(Object.entries(groupedConversations) as [DateGroup, Conversation[]][]).map(
                ([group, items]) => {
                  if (items.length === 0) return null
                  return (
                    <div key={group} className="space-y-1.5">
                      <h3 className="px-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70 flex items-center gap-1.5">
                        <History className="w-3 h-3" />
                        {group}
                      </h3>
                      <div className="space-y-1">
                        {items.map((conversation) => (
                          <ConversationItem
                            key={conversation.uuid}
                            conversation={conversation}
                            isActive={conversation.uuid === currentConversationId}
                            onSelect={() => onSelectConversation(conversation.uuid)}
                            onDelete={() => onDeleteConversation(conversation.uuid)}
                            onUpdate={onUpdateConversation}
                          />
                        ))}
                      </div>
                    </div>
                  )
                },
              )}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

interface ConversationItemProps {
  conversation: Conversation
  isActive: boolean
  onSelect: () => void
  onDelete: () => void
  onUpdate?: (id: string, title: string) => Promise<void>
}

function ConversationItem({
  conversation,
  isActive,
  onSelect,
  onDelete,
  onUpdate,
}: ConversationItemProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState(conversation.title)
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async () => {
    if (!onUpdate || !editTitle.trim()) return

    setIsSaving(true)
    try {
      await onUpdate(conversation.uuid, editTitle.trim())
      setIsEditing(false)
      toast.success('Conversation renamed')
    } catch (error) {
      console.error('Failed to update title:', error)
      toast.error('Failed to rename conversation')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setEditTitle(conversation.title)
    setIsEditing(false)
  }

  return (
    <div
      className={cn(
        'group flex items-center gap-2 px-3 py-2.5 rounded-lg transition-all relative cursor-pointer',
        'hover:bg-accent/60',
        isActive && 'bg-accent shadow-sm ring-1 ring-border/50',
      )}
      onClick={onSelect}
    >
      {isEditing ? (
        /* Edit mode */
        <div className="flex-1 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <MessageSquare
            className={cn(
              'w-3.5 h-3.5 flex-shrink-0',
              isActive ? 'text-primary' : 'text-muted-foreground',
            )}
          />
          <Input
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave()
              if (e.key === 'Escape') handleCancel()
            }}
            className="h-7 text-xs px-2"
            autoFocus
            disabled={isSaving}
          />
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 flex-shrink-0 hover:bg-success/10 hover:text-success"
              onClick={handleSave}
              disabled={isSaving}
            >
              <Check className="w-3.5 h-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 flex-shrink-0 hover:bg-destructive/10 hover:text-destructive"
              onClick={handleCancel}
              disabled={isSaving}
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      ) : (
        /* Display mode */
        <>
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2.5">
              <MessageSquare
                className={cn(
                  'w-3.5 h-3.5 mt-0.5 flex-shrink-0 transition-colors',
                  isActive
                    ? 'text-primary'
                    : 'text-muted-foreground group-hover:text-foreground/70',
                )}
              />
              <div className="flex-1 min-w-0">
                <p
                  className={cn(
                    'text-xs font-medium leading-tight break-words line-clamp-2 transition-colors',
                    isActive ? 'text-foreground' : 'text-foreground/80 group-hover:text-foreground',
                  )}
                  title={conversation.title}
                >
                  {conversation.title}
                </p>
                <p className="text-[10px] text-muted-foreground/70 mt-1">
                  {formatDistanceToNow(new Date(conversation.updated_at), {
                    addSuffix: true,
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Action Menu */}
          <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 hover:bg-accent-foreground/10"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="w-3.5 h-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                {onUpdate && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsEditing(true)
                    }}
                  >
                    <Pencil className="w-3.5 h-3.5 mr-2" />
                    <span>Rename</span>
                  </DropdownMenuItem>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive focus:bg-destructive/10"
                      onSelect={(e) => e.preventDefault()} // Prevent closing before dialog
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-2" />
                      <span>Delete</span>
                    </DropdownMenuItem>
                  </AlertDialogTrigger>
                  <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete conversation?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete the conversation and all its messages.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel onClick={(e) => e.stopPropagation()}>
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={(e) => {
                          e.stopPropagation()
                          onDelete()
                        }}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </>
      )}
    </div>
  )
}
